import React from "react";
import { EventTreeTarget } from "~/common/constants";
import { useAnalytics } from "~/components/Analytics";
import { ContentItem } from "~/components/ContentItem";
import { ContentItemGrid } from "~/components/ContentItemGrid";
import { ContentItemWithImage } from "~/components/ContentItemWithImage";
import { ReadMorePosts, ReadMorePostsProps } from "~/components/ReadMorePosts";
import {
  VerticalPageHeaderSection,
  VerticalPageHeaderSectionProps,
} from "~/components/VerticalPageHeaderSection";
import { createClickEventTreeHandler } from "~/lib/client/createEventTreeHandlers";
import { Body, Headline } from "~/ui-library";

import {
  isFourColumnContentBlock,
  isGridComponentContentBlock,
  isGroupOf3ContentBlock,
  VerticalContentTypes,
} from "~/components/VerticalPage/vertical-type-guards";
import { ReadMorePostsItems } from "~/components/ReadMorePosts/ReadMorePostsItems";

export type VerticalPageProps = {
  headerProps: {
    mainTitle: string;
    mainBody: string;
    leftHeroProps: VerticalPageHeaderSectionProps;
    centerHeroProps: VerticalPageHeaderSectionProps;
    rightHeroProps: VerticalPageHeaderSectionProps;
  };
  content: VerticalContentTypes[];
  readMorePostsProps: Omit<ReadMorePostsProps, "renderItems"> | null;
};

export type VerticalContentBlocksProps = {
  verticalContentBlocks: VerticalContentTypes[];
};

const VerticalContentBlocks: React.FC<VerticalContentBlocksProps> = ({
  verticalContentBlocks,
}) => {
  const verticalContentComponents = verticalContentBlocks.map(
    (contentBlock, index): JSX.Element | null => {
      if (isGroupOf3ContentBlock(contentBlock)) {
        return (
          <div className="col-span-12 grid" key={index}>
            <ContentItemGrid
              title={contentBlock.title}
              variant={"3-item-row"}
              key={index}
              itemBorder
            >
              {contentBlock.posts.map((apiData, innerIndex) => (
                <ContentItemWithImage
                  {...apiData}
                  imagePosition="top"
                  key={innerIndex}
                  kicker={
                    contentBlock.displayPrimaryTag ? apiData.kicker : null
                  }
                  body=""
                  footer={contentBlock.displayMetadata ? apiData.footer : null}
                  displayDate={contentBlock.displayDate}
                />
              ))}
            </ContentItemGrid>
          </div>
        );
      }
      if (isFourColumnContentBlock(contentBlock)) {
        return (
          <div className="col-span-12" key={index}>
            <ContentItemGrid
              title={contentBlock.title}
              variant={"4-item-row"}
              key={index}
            >
              {contentBlock.posts.map((apiData, innerIndex) => (
                <ContentItemWithImage
                  imagePosition="top"
                  {...apiData}
                  key={innerIndex}
                  kicker={
                    contentBlock.displayPrimaryTag ? apiData.kicker : null
                  }
                  body=""
                  footer={contentBlock.displayMetadata ? apiData.footer : null}
                  displayDate={contentBlock.displayDate}
                />
              ))}
            </ContentItemGrid>
          </div>
        );
      }
      if (isGridComponentContentBlock(contentBlock)) {
        return (
          <React.Fragment key={index}>
            <div className="col-span-12 md:col-span-4">
              <ContentItemGrid
                title={contentBlock.groupLeft.title}
                variant={"column"}
                itemBorder
              >
                {contentBlock.groupLeft.posts.map((apiData, innerIndex) => (
                  <ContentItem
                    {...apiData}
                    key={innerIndex}
                    kicker={
                      contentBlock.displayPrimaryTag ? apiData.kicker : null
                    }
                    body=""
                    footer={
                      contentBlock.displayMetadata ? apiData.footer : null
                    }
                    displayDate={contentBlock.displayDate}
                  />
                ))}
              </ContentItemGrid>
            </div>
            <div className="col-span-12 md:col-span-8">
              <ContentItemGrid
                title={contentBlock.groupRight.title}
                variant={"column"}
                itemBorder
              >
                {contentBlock.groupRight.posts.map((apiData, innerIndex) => (
                  <ContentItemWithImage
                    imagePosition="left"
                    {...apiData}
                    key={innerIndex}
                    kicker={
                      contentBlock.displayPrimaryTag ? apiData.kicker : null
                    }
                    body=""
                    footer={
                      contentBlock.displayMetadata ? apiData.footer : null
                    }
                    displayDate={contentBlock.displayDate}
                  />
                ))}
              </ContentItemGrid>
            </div>
          </React.Fragment>
        );
      }
      return null;
    }
  );
  return <>{verticalContentComponents}</>;
};

export const VerticalPage: React.FC<VerticalPageProps> = ({
  headerProps,
  readMorePostsProps,
  content,
}) => {
  const { analyticsEnhancedTrack } = useAnalytics();

  const clickHandler = createClickEventTreeHandler(
    [EventTreeTarget.CONTENT_ITEM_KICKER_LINK],
    (matchingOrigin: EventTreeTarget, matchingElement: HTMLElement) => {
      analyticsEnhancedTrack("Vertical Page: Click Content Kicker", {
        kicker: matchingElement.textContent,
      });
    }
  );

  return (
    <div className="contents" onClick={clickHandler}>
      <div className="flex w-full">
        <div className="mx-[auto] max-w-[1300px] px-s py-m lg:pt-l">
          <div className="mb-m border-b-1 border-gray-300">
            <Headline className="pb-s" as="h1" level={0}>
              {headerProps.mainTitle}
            </Headline>
            <Body as="p" size="large" className="max-w-[925px] pb-l">
              {headerProps.mainBody}
            </Body>
          </div>
          <div className="grid max-w-[1300px] grid-cols-12 gap-m gap-y-xl pb-xl">
            <div className="order-2 col-span-12 lg:order-1 lg:col-span-3">
              <VerticalPageHeaderSection {...headerProps.leftHeroProps} />
            </div>
            <div className="order-1 col-span-12 w-full lg:order-2 lg:col-span-6">
              <VerticalPageHeaderSection {...headerProps.centerHeroProps} />
            </div>
            <div className="order-3 col-span-12 lg:order-3 lg:col-span-3">
              <VerticalPageHeaderSection {...headerProps.rightHeroProps} />
            </div>
          </div>
          <div className="grid max-w-[1300px] grid-cols-12 gap-m gap-y-xl">
            <VerticalContentBlocks verticalContentBlocks={content} />
          </div>

          {readMorePostsProps ? (
            <div className="py-xl">
              <div>
                <ReadMorePosts
                  {...readMorePostsProps}
                  renderItems={(renderProps) => (
                    <ReadMorePostsItems
                      {...renderProps}
                      className="gap-y-xl"
                      imagePosition="top"
                      title={readMorePostsProps.title ?? "Keep Reading"}
                      variant="4-item-row"
                    />
                  )}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
